import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, Plus, Search, Trash2 } from "lucide-react";

const RLS_OPERATORS = [
  { value: "eq", label: "Equals" },
  { value: "neq", label: "Not Equals" },
  { value: "in", label: "In" },
  { value: "not_in", label: "Not In" },
  { value: "gt", label: "Greater Than" },
  { value: "gte", label: "Greater or Equal" },
  { value: "lt", label: "Less Than" },
  { value: "lte", label: "Less or Equal" },
  { value: "between", label: "Between" },
  { value: "is_null", label: "Is Null" },
  { value: "is_not_null", label: "Is Not Null" },
];

const OPERATOR_LABELS = Object.fromEntries(RLS_OPERATORS.map((x) => [x.value, x.label]));

const MASK_TYPES = [
  { value: "token", label: "Tokenize", example: "tok_a13f7c9d2e1b" },
  { value: "partial_email", label: "Partial Email", example: "ra***@company.com" },
  { value: "last4", label: "Last 4 Digits", example: "******1234" },
  { value: "fixed", label: "Fixed Value", example: "*** or REDACTED" },
  { value: "null", label: "Null Out", example: "null" },
];
const MASK_TYPE_MAP = Object.fromEntries(MASK_TYPES.map((x) => [x.value, x]));

const NULL_OPS = new Set(["is_null", "is_not_null"]);
const LIST_OPS = new Set(["in", "not_in"]);
const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 300;

function normalizeListValue(value) {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value === undefined || value === null) return [];
  return String(value)
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function normalizeBetweenValue(value) {
  if (Array.isArray(value)) return [value[0] ?? "", value[1] ?? ""];
  if (value === undefined || value === null) return ["", ""];
  const parts = String(value)
    .split(",")
    .map((v) => v.trim())
    .slice(0, 2);
  return [parts[0] || "", parts[1] || ""];
}

function normalizeScalarValue(value) {
  if (Array.isArray(value)) return value[0] ? String(value[0]) : "";
  if (value === undefined || value === null) return "";
  return String(value);
}

function normalizeValueByOperator(value, operator) {
  if (NULL_OPS.has(operator)) return "";
  if (operator === "between") return normalizeBetweenValue(value);
  if (LIST_OPS.has(operator)) return normalizeListValue(value);
  return normalizeScalarValue(value);
}

function DateValueInput({ value, onChange, className = "" }) {
  return (
    <input
      type="date"
      value={normalizeScalarValue(value)}
      onChange={(e) => onChange?.(e.target.value)}
      className={`h-9 px-2 rounded-md border border-border bg-background text-xs ${className}`}
    />
  );
}

function DateRangeInput({ value, onChange, className = "" }) {
  const [from, to] = normalizeBetweenValue(value);
  return (
    <div className={`grid grid-cols-2 gap-2 ${className}`}>
      <input
        type="date"
        value={from}
        onChange={(e) => onChange?.([e.target.value, to])}
        className="h-9 px-2 rounded-md border border-border bg-background text-xs"
      />
      <input
        type="date"
        value={to}
        onChange={(e) => onChange?.([from, e.target.value])}
        className="h-9 px-2 rounded-md border border-border bg-background text-xs"
      />
    </div>
  );
}

export function InlineDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  className = "",
  buttonClassName = "",
  menuClassName = "",
  disabled = false,
  renderRightMeta,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const normalizedOptions = useMemo(
    () =>
      options.map((opt) => {
        if (typeof opt === "string") return { value: opt, label: opt };
        return {
          value: String(opt?.value ?? ""),
          label: String(opt?.label ?? opt?.value ?? ""),
          meta: opt?.meta,
        };
      }),
    [options]
  );

  const selected = normalizedOptions.find((opt) => String(opt.value) === String(value));

  useEffect(() => {
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={`w-full h-9 px-2 rounded-md border border-border bg-background text-xs flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed ${buttonClassName}`}
      >
        <span className={`truncate text-left ${selected ? "" : "text-muted-foreground"}`}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !disabled && (
        <div className={`absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg overflow-hidden ${menuClassName}`}>
          <div className="max-h-56 overflow-y-auto p-1">
            {normalizedOptions.length === 0 ? (
              <div className="px-2 py-2 text-[11px] text-muted-foreground">No options</div>
            ) : (
              normalizedOptions.map((opt) => {
                const isSelected = String(value) === String(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange?.(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full px-2 py-1.5 rounded text-xs flex items-center gap-2 transition-colors ${
                      isSelected
                        ? "bg-primary/15 text-primary"
                        : "hover:bg-primary/10 hover:text-primary"
                    }`}
                  >
                    <span className="truncate text-left font-medium">{opt.label}</span>
                    {typeof renderRightMeta === "function" ? renderRightMeta(opt) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ValueDropdown({
  mode = "single",
  dataMode = "inline",
  value,
  onChange,
  options = [],
  fetchOptions,
  placeholder = "Select value",
  className = "",
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalEstimate, setTotalEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState("");
  const selectAllAbortRef = useRef(null);

  const selectedValues = mode === "multi" ? normalizeListValue(value) : [normalizeScalarValue(value)].filter(Boolean);

  const isAllSelected =
    mode === "multi" &&
    typeof totalEstimate === "number" &&
    totalEstimate > 0 &&
    selectedValues.length >= totalEstimate;

  const inlineItems = useMemo(() => options.map((x) => String(x)), [options]);

  const filteredInlineItems = useMemo(() => {
    if (dataMode !== "inline") return [];
    if (!query.trim()) return inlineItems;
    const q = query.trim().toLowerCase();
    return inlineItems.filter((x) => x.toLowerCase().includes(q));
  }, [dataMode, inlineItems, query]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setActiveQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, open]);

  const loadAsync = async ({ reset = false, q = "", cursor = 0 } = {}) => {
    if (typeof fetchOptions !== "function") return;
    if (reset) {
      setLoading(true);
      setError("");
    } else {
      setLoadingMore(true);
    }
    try {
      const data = await fetchOptions({ q, cursor, limit: PAGE_LIMIT });
      const nextItems = Array.isArray(data?.items) ? data.items.map((x) => String(x)) : [];
      const next = data?.nextCursor ?? null;
      const total = typeof data?.totalEstimate === "number" ? data.totalEstimate : null;

      setItems((prev) => {
        if (reset) return nextItems;
        return [...new Set([...prev, ...nextItems])];
      });
      setNextCursor(next);
      setTotalEstimate(total);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load values");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!open || dataMode !== "async") return;
    loadAsync({ reset: true, q: activeQuery, cursor: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dataMode, activeQuery]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open) return;
    if (selectAllAbortRef.current) {
      selectAllAbortRef.current.abort();
      selectAllAbortRef.current = null;
      setLoadingAll(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (selectAllAbortRef.current) {
        selectAllAbortRef.current.abort();
      }
    };
  }, []);

  const renderItems = dataMode === "inline" ? filteredInlineItems : items;

  const toggleMultiValue = (nextValue) => {
    const exists = selectedValues.includes(nextValue);
    const next = exists ? selectedValues.filter((v) => v !== nextValue) : [...selectedValues, nextValue];
    onChange?.(next);
  };

  const onSelectSingle = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
  };

  const selectedLabel =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.length <= 2
      ? selectedValues.join(", ")
      : `${selectedValues.slice(0, 2).join(", ")} +${selectedValues.length - 2}`;

  const handleSelectAll = async () => {
    if (mode !== "multi") return;
    if (dataMode === "inline") {
      const all = [...new Set(inlineItems)];
      if (all.length === selectedValues.length) {
        onChange?.([]);
      } else {
        onChange?.(all);
      }
      return;
    }

    if (isAllSelected) {
      onChange?.([]);
      return;
    }

    if (typeof fetchOptions !== "function") return;

    if (selectAllAbortRef.current) {
      selectAllAbortRef.current.abort();
      selectAllAbortRef.current = null;
    }

    const controller = new AbortController();
    selectAllAbortRef.current = controller;
    setLoadingAll(true);
    setError("");
    try {
      // Global selection ignores current search by design and requests all values in one call.
      const data = await fetchOptions({
        q: "",
        cursor: 0,
        limit: PAGE_LIMIT,
        all: true,
        signal: controller.signal,
      });
      const unique = [...new Set(Array.isArray(data?.items) ? data.items.map((x) => String(x)) : [])];
      onChange?.(unique);
      setItems((prev) => [...new Set([...prev, ...unique])]);
      if (typeof data?.totalEstimate === "number") {
        setTotalEstimate(data.totalEstimate);
      } else if (typeof totalEstimate === "number" && totalEstimate < unique.length) {
        setTotalEstimate(unique.length);
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setError(err?.response?.data?.error || "Failed to select all values");
    } finally {
      if (selectAllAbortRef.current === controller) {
        selectAllAbortRef.current = null;
      }
      setLoadingAll(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full h-9 px-2 rounded-md border border-border bg-background text-xs flex items-center justify-between"
      >
        <span className={`truncate text-left ${selectedValues.length === 0 ? "text-muted-foreground" : ""}`}>{selectedLabel}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
          <div className="p-2 border-b border-border space-y-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search values..."
                className="w-full h-8 pl-7 pr-2 rounded-md border border-border bg-background text-xs"
              />
            </div>
            {mode === "multi" && (
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={loadingAll}
                  className="text-[11px] text-primary hover:opacity-90 disabled:opacity-60"
                >
                  {loadingAll ? "Selecting all..." : isAllSelected ? "Deselect all" : "Select all"}
                </button>
                <span className="text-[11px] text-muted-foreground">{selectedValues.length} selected</span>
              </div>
            )}
          </div>

          <div className="max-h-44 overflow-y-auto p-1">
            {loading ? (
              <div className="px-2 py-3 text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> Loading...
              </div>
            ) : error ? (
              <div className="px-2 py-2 text-[11px] text-destructive">{error}</div>
            ) : renderItems.length === 0 ? (
              <div className="px-2 py-2 text-[11px] text-muted-foreground">No options</div>
            ) : (
              renderItems.map((opt) =>
                mode === "single" ? (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => onSelectSingle(opt)}
                    className={`w-full px-2 py-1.5 text-left text-xs rounded truncate transition-colors ${
                      selectedValues.includes(opt)
                        ? "bg-primary/15 text-primary"
                        : "hover:bg-primary/10 hover:text-primary"
                    }`}
                  >
                    {opt}
                  </button>
                ) : (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleMultiValue(opt)}
                    className={`w-full px-2 py-1.5 text-left text-xs rounded inline-flex items-center gap-2 transition-colors ${
                      selectedValues.includes(opt)
                        ? "bg-primary/15 text-primary"
                        : "hover:bg-primary/10 hover:text-primary"
                    }`}
                  >
                    <input type="checkbox" checked={selectedValues.includes(opt)} readOnly className="h-3.5 w-3.5" />
                    <span className="truncate">{opt}</span>
                  </button>
                )
              )
            )}
          </div>

          <div className="border-t border-border p-1.5 flex items-center justify-between">
            {dataMode === "async" && nextCursor !== null ? (
              <button
                type="button"
                onClick={() => loadAsync({ reset: false, q: activeQuery, cursor: nextCursor })}
                disabled={loadingMore}
                className="text-[11px] text-primary hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-1"
              >
                {loadingMore && <Loader2 size={11} className="animate-spin" />}
                Show more
              </button>
            ) : (
              <span className="text-[11px] text-muted-foreground">
                {typeof totalEstimate === "number" ? `${totalEstimate} total` : ""}
              </span>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] text-primary hover:opacity-90"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MaskTypeDropdown({ value, onChange, className = "" }) {
  const selected = MASK_TYPE_MAP[value] || MASK_TYPES[0];
  const options = MASK_TYPES.map((mt) => ({ value: mt.value, label: mt.label, meta: mt.example }));
  return (
    <InlineDropdown
      value={selected.value}
      onChange={onChange}
      options={options}
      className={className}
      renderRightMeta={(opt) => (
        <span className="ml-auto text-[10px] leading-4 text-sky-400 whitespace-nowrap">e.g. {opt.meta}</span>
      )}
    />
  );
}

export function RlsBuilder({
  value,
  onChange,
  tableColumns = [],
  columnProfiles = {},
  fetchColumnValues,
}) {
  const rows = value?.conditions || [];
  const logicalOp = value?.logicalOp || "and";

  const updateRows = (nextRows) => onChange?.({ logicalOp, conditions: nextRows });

  const updateRow = (id, patch) => {
    updateRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => updateRows(rows.filter((r) => r.id !== id));

  const getProfile = (columnName) => columnProfiles[columnName] || {};
  const getFilterType = (columnName) => String(getProfile(columnName)?.filter_type || "").toLowerCase();
  const getValueMode = (columnName) => String(getProfile(columnName)?.value_mode || "").toLowerCase();
  const getInlineOptions = (columnName) => {
    const raw = getProfile(columnName)?.preview_options;
    return Array.isArray(raw) ? raw.map((x) => String(x)) : [];
  };

  const addRow = () => {
    const column = tableColumns[0] || "";
    const operator = (getProfile(column)?.supported_operators || ["eq"])[0] || "eq";
    updateRows([
      ...rows,
      {
        id: `rr_${Date.now()}`,
        column,
        operator,
        value: normalizeValueByOperator("", operator),
      },
    ]);
  };

  const isDate = (row) => getFilterType(row.column) === "date";
  const isCategoryOrText = (row) => {
    const ft = getFilterType(row.column);
    return ft === "categorical" || ft === "text";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">RLS Conditions</h4>
        <div className="flex items-center gap-2">
          <InlineDropdown
            value={logicalOp}
            onChange={(next) => onChange?.({ logicalOp: next, conditions: rows })}
            options={[
              { value: "and", label: "AND" },
              { value: "or", label: "OR" },
            ]}
            className="w-[102px]"
            buttonClassName="h-8"
          />
          <button
            type="button"
            onClick={addRow}
            className="h-8 px-2 rounded-md border border-border text-xs inline-flex items-center gap-1 hover:bg-muted"
          >
            <Plus size={12} /> Add Condition
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3">
          Add at least one condition.
        </div>
      ) : (
        rows.map((row) => {
          const supportedOps = getProfile(row.column)?.supported_operators || RLS_OPERATORS.map((op) => op.value);
          const valueMode = getValueMode(row.column);
          const useAsyncLookup = valueMode === "lookup_api";
          const inlineOptions = getInlineOptions(row.column);

          return (
            <div key={row.id} className="grid grid-cols-12 gap-2 items-start">
              <InlineDropdown
                value={row.column}
                onChange={(nextColumn) => {
                  const nextSupported = getProfile(nextColumn)?.supported_operators || ["eq"];
                  const nextOperator = nextSupported.includes(row.operator) ? row.operator : nextSupported[0];
                  updateRow(row.id, {
                    column: nextColumn,
                    operator: nextOperator,
                    value: normalizeValueByOperator(row.value, nextOperator),
                  });
                }}
                options={[
                  { value: "", label: "Select column" },
                  ...tableColumns.map((c) => ({ value: c, label: c })),
                ]}
                className="col-span-4"
              />

              <InlineDropdown
                value={row.operator}
                onChange={(nextOperator) => {
                  updateRow(row.id, {
                    operator: nextOperator,
                    value: normalizeValueByOperator(row.value, nextOperator),
                  });
                }}
                options={supportedOps.map((opValue) => ({
                  value: opValue,
                  label: OPERATOR_LABELS[opValue] || opValue,
                }))}
                className="col-span-3"
              />

              {NULL_OPS.has(row.operator) ? (
                <input
                  value=""
                  placeholder="No value needed"
                  disabled
                  className="col-span-4 h-9 px-2 rounded-md border border-border bg-background text-xs"
                />
              ) : isDate(row) && row.operator === "between" ? (
                <DateRangeInput
                  value={row.value}
                  onChange={(next) => updateRow(row.id, { value: next })}
                  className="col-span-4"
                />
              ) : isDate(row) ? (
                <DateValueInput
                  value={row.value}
                  onChange={(next) => updateRow(row.id, { value: next })}
                  className="col-span-4"
                />
              ) : isCategoryOrText(row) && LIST_OPS.has(row.operator) ? (
                <ValueDropdown
                  mode="multi"
                  dataMode={useAsyncLookup ? "async" : "inline"}
                  options={inlineOptions}
                  fetchOptions={
                    useAsyncLookup && typeof fetchColumnValues === "function"
                      ? ({ q, cursor, limit }) =>
                          fetchColumnValues({
                            columnName: row.column,
                            q,
                            cursor,
                            limit,
                          })
                      : undefined
                  }
                  value={row.value}
                  onChange={(next) => updateRow(row.id, { value: next })}
                  placeholder="Select values"
                  className="col-span-4"
                />
              ) : isCategoryOrText(row) && (row.operator === "eq" || row.operator === "neq") ? (
                <ValueDropdown
                  mode="single"
                  dataMode={useAsyncLookup ? "async" : "inline"}
                  options={inlineOptions}
                  fetchOptions={
                    useAsyncLookup && typeof fetchColumnValues === "function"
                      ? ({ q, cursor, limit }) =>
                          fetchColumnValues({
                            columnName: row.column,
                            q,
                            cursor,
                            limit,
                          })
                      : undefined
                  }
                  value={row.value}
                  onChange={(next) => updateRow(row.id, { value: next })}
                  placeholder="Select value"
                  className="col-span-4"
                />
              ) : (
                <input
                  value={Array.isArray(row.value) ? row.value.join(",") : normalizeScalarValue(row.value)}
                  onChange={(e) => updateRow(row.id, { value: e.target.value })}
                  placeholder={
                    row.operator === "between"
                      ? "e.g. 100,500"
                      : row.operator === "in" || row.operator === "not_in"
                      ? "e.g. west,east"
                      : "Value"
                  }
                  className="col-span-4 h-9 px-2 rounded-md border border-border bg-background text-xs"
                />
              )}

              <button
                type="button"
                onClick={() => removeRow(row.id)}
                className="col-span-1 h-9 rounded-md border border-red-300 text-red-600 flex items-center justify-center hover:bg-red-50 hover:border-red-400"
              >
                <Trash2 size={13} className="text-red-600" />
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}

export function MaskingBuilder({ value, onChange, tableColumns = [], columnProfiles = {} }) {
  const rows = value?.rules || [];

  const updateRows = (nextRows) => onChange?.({ rules: nextRows });

  const addRule = () => {
    updateRows([
      ...rows,
      {
        id: `mr_${Date.now()}`,
        column: tableColumns[0] || "",
        maskType: "token",
        maskValue: "",
      },
    ]);
  };

  const updateRow = (id, patch) => {
    updateRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => updateRows(rows.filter((r) => r.id !== id));

  const getColumnType = (columnName) => {
    const ft = String(columnProfiles?.[columnName]?.filter_type || "").toLowerCase();
    if (ft === "numeric") return "numeric";
    if (ft === "date") return "date";
    if (ft === "text" || ft === "categorical") return "text";
    const dbType = String(columnProfiles?.[columnName]?.db_type || "").toLowerCase();
    if (/(int|decimal|numeric|float|double|real|number)/.test(dbType)) return "numeric";
    if (/(date|time|timestamp)/.test(dbType)) return "date";
    return "text";
  };

  const getMaskWarning = (maskType, columnName) => {
    const type = getColumnType(columnName);
    const mt = String(maskType || "").toLowerCase();

    if (mt === "null") {
      return "Warning: This column will be excluded from calculations, filters, and joins. It provides zero analytical value.";
    }

    if (mt === "fixed") {
      if (type === "numeric") {
        return "Warning: Mathematical aggregations (SUM, AVG) will be disabled or return placeholder values (for example 0).";
      }
      if (type === "date") {
        return "Warning: Time-based trends (MoM, YoY) will be lost because all records map to the same placeholder date.";
      }
      return "Warning: COUNT(DISTINCT) and GROUP BY can collapse values into a single placeholder bucket, reducing analysis quality.";
    }

    if (mt === "token") {
      if (type === "numeric") {
        return "Critical: Hashed numeric values cannot be used for SUM/AVG style mathematical operations.";
      }
      if (type === "date") {
        return "Warning: Grouping can work, but date functions (YEAR/MONTH/EXTRACT) may fail unless applied before hashing.";
      }
      return "";
    }

    if (mt === "last4" || mt === "partial_email") {
      if (type === "numeric") {
        return "Warning: Value behaves like text after partial reveal. Mathematical operations are no longer valid.";
      }
      if (type === "date") {
        return "Warning: Partial reveal is not suitable for date/time columns and can break trend analysis.";
      }
      return "Warning: Partial masking can cause collisions; COUNT(DISTINCT) may undercount when multiple values share the same visible pattern.";
    }

    return "";
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Masking Rules</h4>
        <button
          type="button"
          onClick={addRule}
          className="h-8 px-2 rounded-md border border-border text-xs inline-flex items-center gap-1 hover:bg-muted"
        >
          <Plus size={12} /> Add Rule
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3">
          Add at least one mask rule.
        </div>
      ) : (
        rows.map((row) => {
          const rowWarning = getMaskWarning(row.maskType, row.column);
          return (
          <div key={row.id} className="space-y-1.5">
            <div className="grid grid-cols-12 gap-2 items-center">
              <InlineDropdown
                value={row.column}
                onChange={(nextColumn) => updateRow(row.id, { column: nextColumn })}
                options={[
                  { value: "", label: "Select column" },
                  ...tableColumns.map((c) => ({ value: c, label: c })),
                ]}
                className="col-span-4"
              />

              <MaskTypeDropdown
                value={row.maskType}
                onChange={(nextType) =>
                  updateRow(row.id, { maskType: nextType, maskValue: nextType === "fixed" ? row.maskValue : "" })
                }
                className={row.maskType === "fixed" ? "col-span-4" : "col-span-7"}
              />

              {row.maskType === "fixed" && (
                <input
                  value={row.maskValue}
                  onChange={(e) => updateRow(row.id, { maskValue: e.target.value })}
                  placeholder="Replacement value (optional)"
                  className="col-span-3 h-9 px-2 rounded-md border border-border bg-background text-xs"
                />
              )}

              <button
                type="button"
                onClick={() => removeRow(row.id)}
                className="col-span-1 h-9 rounded-md border border-red-300 text-red-600 flex items-center justify-center hover:bg-red-50 hover:border-red-400"
              >
                <Trash2 size={13} className="text-red-600" />
              </button>
            </div>
            {row.maskType === "fixed" && (
              <p className="text-[11px] text-muted-foreground">
                Optional parameter sets the replacement text for all values in this column (example: <code>REDACTED</code>). If left blank, default output is <code>***</code>.
              </p>
            )}
            {rowWarning && (
              <p className="text-[11px] text-red-500 dark:text-red-400">
                {rowWarning}
              </p>
            )}
          </div>
        )})
      )}
    </div>
  );
}
